import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { type SvgIconComponent } from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import { INNER_RADIUS, TEXT } from "../utils/tokens";
import { ReassistButton } from "./ReassistButton";

/**
 * 0件の面（方針書 2-7）。白地と1px の破線の枠に、40px のアイコン、16px の太字の1文、14px の補足、
 * 必要なら次の操作のボタン1つを中央にそろえて出す。
 */
export const EmptyBlock: React.FC<{
  icon: SvgIconComponent;
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** ダイアログの中など、狭い場所で上下の余白を詰める。 */
  compact?: boolean;
}> = ({ icon: Icon, title, description, action, compact = false }) => (
  <Stack
    sx={(theme) => ({
      boxSizing: "border-box",
      alignItems: "center",
      textAlign: "center",
      backgroundColor: theme.palette.white.main,
      border: `1px dashed ${theme.palette.border.main}`,
      borderRadius: "8px",
      px: { xs: 2, [MOBILE_BREAKPOINT]: 3 },
      py: compact ? 2 : { xs: 3, [MOBILE_BREAKPOINT]: 4 },
    })}
  >
    <Icon aria-hidden sx={{ fontSize: 40, color: "primary.main" }} />
    <Typography
      sx={{
        ...TEXT.cardTitle,
        mt: 1.5,
        color: "text.primary",
        textWrap: "balance",
      }}
    >
      {title}
    </Typography>
    {description !== undefined && (
      <Typography
        sx={{
          ...TEXT.body,
          mt: 0.5,
          color: "text.secondary",
          textWrap: "balance",
        }}
      >
        {description}
      </Typography>
    )}
    {action !== undefined && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Stack>
);

/**
 * 取得に失敗したときの表示（方針書 2-7）。白地、左端に 4px の error.main の線、ErrorOutline、14px の文。
 * onRetry を渡すと「再読み込み」（outlined、小）を添える。文言は呼び出し側が全文を渡す。
 */
export const LoadFailed: React.FC<{
  message: string;
  onRetry?: () => void;
}> = ({ message, onRetry }) => (
  <Box
    role="alert"
    sx={(theme) => ({
      boxSizing: "border-box",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      columnGap: 2,
      rowGap: 1.5,
      px: 2,
      py: 1.5,
      backgroundColor: theme.palette.white.main,
      border: `1px solid ${theme.palette.border.main}`,
      borderLeft: `4px solid ${theme.palette.error.main}`,
      borderRadius: INNER_RADIUS,
    })}
  >
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
        flex: "1 1 240px",
      }}
    >
      <ErrorOutline
        aria-hidden
        sx={{ fontSize: 20, mt: "2px", flexShrink: 0, color: "error.main" }}
      />
      <Typography sx={{ ...TEXT.body, color: "text.primary" }}>
        {message}
      </Typography>
    </Box>
    {onRetry !== undefined && (
      <ReassistButton
        label="再読み込み"
        variant="outlined"
        size="small"
        onClick={onRetry}
      />
    )}
  </Box>
);

/** 読み込み中。最終的な形に近い Skeleton と、読み上げ用の文言を出す。 */
export const LoadingCards: React.FC<{
  rows: number;
  /** カードの高さ（px）。 */
  height: number | Record<string, number>;
  radius?: string;
}> = ({ rows, height, radius = "8px" }) => (
  <Box role="status" aria-live="polite" aria-busy="true">
    <Box component="span" sx={visuallyHidden}>
      読み込み中です
    </Box>
    <Stack spacing={1.5}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton
          key={i}
          variant="rounded"
          animation="wave"
          sx={{
            // 動きを減らす設定のときは波のアニメーションを止める
            "@media (prefers-reduced-motion: reduce)": {
              "&::after": { animation: "none" },
            },
            borderRadius: radius,
            height,
          }}
        />
      ))}
    </Stack>
  </Box>
);
